import {registerTemplateGraphNode} from "#template/template-node/register"
import {meshGeometryAttributeSetLike, meshGeometryAttributeSetLikeMarker} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {meshGeometryAttributeLike} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {MeshGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"

import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"

import type {IModifyTemplateGraph} from "#template/interactions"
import type {AttributeSetRecordInputParameters} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"
import {
    GeometryAttributeSetRecordInputNode,
    attributeSetRecordInputParametersSchema,
} from "#template/nodes/geometry-graph/attribute-set/attribute-set-record-input/geometry-attribute-set-record-input-node"

export type MeshGeometryAttributeSetRecordInputParameters = AttributeSetRecordInputParameters<MeshGeometryAttributeLikeNode, MeshGeometryAttributeSetLikeNode>

export interface ModifyMeshGeometryAttributeSetRecordInputLikeNode {
    addRecordEntry(key: string, value: MeshGeometryAttributeLikeNode | MeshGeometryAttributeSetLikeNode | null, modifyTemplateGraph: IModifyTemplateGraph): void
    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void
    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void
}

const definition: TemplateNodeDefinition<MeshGeometryAttributeSetRecordInputParameters> = {
    nodeClass: "MeshGeometryAttributeSetRecordInput",
    validation: {
        parameters: attributeSetRecordInputParametersSchema(meshGeometryAttributeLike, meshGeometryAttributeSetLike),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MeshGeometryAttributeSetRecordInput
    extends GeometryAttributeSetRecordInputNode<2, MeshGeometryAttributeLikeNode, MeshGeometryAttributeSetLikeNode>
    implements MeshGeometryAttributeSetLikeNode, ModifyMeshGeometryAttributeSetRecordInputLikeNode
{
    get [meshGeometryAttributeSetLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override get outputIndexingDim(): 2 {
        return 2
    }
    addRecordEntry(
        key: string,
        value: MeshGeometryAttributeLikeNode | MeshGeometryAttributeSetLikeNode | null,
        modifyTemplateGraph: IModifyTemplateGraph,
    ): void {
        modifyTemplateGraph.modifyTemplateGraph(() => this.updateParameters({record: {...this.parameters.record, [key]: value}}))
    }

    removeRecordEntry(key: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            delete newRecord[key]
            this.updateParameters({record: newRecord})
        })
    }

    renameRecordEntry(oldKey: string, newKey: string, modifyTemplateGraph: IModifyTemplateGraph): void {
        modifyTemplateGraph.modifyTemplateGraph(() => {
            const newRecord = {...this.parameters.record}
            if (newRecord[oldKey] === undefined) throw new Error(`Key "${oldKey}" does not exist in record`)

            newRecord[newKey] = newRecord[oldKey]
            delete newRecord[oldKey]
            this.updateParameters({record: newRecord})
        })
    }
}
